import { keyinPaymentInfo } from 'api/Api';
import KeyinForm from 'app/components/KeyinForm';
import LoadingIndicator from 'app/components/LoadingIndicator';
import useModals from 'app/components/Modal/manager/useModal';
import PriceContent from 'app/components/PriceContent';
import IframeBridge from 'IframeBridge';
import { usePaymentState } from 'payment_state/PaymentStateContext';
import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

interface RequestFields {
  orderId: string;
  productName: string;
  requestCurrency: string;
  requestAmount: number;
  returnUrl: string;
  customerId?: string;
  customerIp?: string;
  mallReserved?: string;
  webhookUrl?: string;
}

const browserTypeJson = (lang: string) => {
  switch (lang) {
    case 'zh-TW':
      return 'cntr';
    case 'zh-CN':
      return 'cnsi';
    case 'en-US':
      return 'en';
    case 'ko':
      return 'ko';
    case 'ko-KR':
      return 'ko';
    case 'ja':
      return 'jp';
    default:
      return '';
  }
};

const Keyin = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const searchParams = useLocation().search;
  const { paymentState, setPaymentState } = usePaymentState();
  const { openModal } = useModals();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [language, setLanguage] = useState<string>('');
  const [langUse, setLangUse] = useState<string>();
  const [tid, setTid] = useState<string>('');
  const [keypadToken, setKeypadToken] = useState<string>('');
  const [reqBody, setReqBody] = useState<RequestFields>();
  const [keyinType, setKeyinType] = useState<string>('');

  const clearStorage = () => {
    window.sessionStorage.removeItem('auth');
    window.sessionStorage.removeItem('clientKey');
    window.sessionStorage.removeItem('mid');
    window.sessionStorage.removeItem('formSubmitted');
  };

  const updatePaymentState = infoData => {
    setPaymentState(prevState => ({
      ...prevState,
      mid: infoData.mid,
      sign: infoData.sign,
      auth: infoData.auth,
      clientKey: infoData.clientKey,
      orderId: infoData.orderId,
      customerId: infoData.customerId,
      productName: infoData.productName,
      requestCurrency: infoData.requestCurrency,
      requestAmount: infoData.requestAmount,
      reqDate: infoData.reqDate,
      clientIp: infoData.clientIp,
      webhookUrl: infoData.webhookUrl,
      returnUrl: infoData.returnUrl,
      mallReserved: infoData.mallReserved,
      billkeyReq: infoData.billkeyReq,
      appScheme: infoData.appScheme,
    }));
  };

  const getKeyinInfo = useMutation(() => keyinPaymentInfo(reqBody), {
    onSuccess: data => {
      if (String(data.data.resultStatus).toUpperCase() === 'SUCCESS') {
        setKeypadToken(data.data.keypadToken);
        setKeyinType(data.data.keyinType);
        setIsLoading(false);
      } else {
        setPaymentState(prevData => ({
          ...prevData,
          resultCode: data.data.resultCode,
          resultMessage: data.data.resultMessage,
          resultStatus: data.data.resultStatus,
          // tid: data.data.tid,
        }));
        navigate('/error');
      }
    },
    onError: error => {
      navigate('/error');
    },
  });

  useEffect(() => {
    clearStorage();

    const params = new URLSearchParams(searchParams);
    const mid = params.get('mid');
    const auth = params.get('auth');
    const sign = params.get('sign');
    const clientKey = params.get('clientKey');
    const orderId = params.get('orderId');
    const customerId = params.get('customerId');
    const productName = params.get('productName');
    const requestAmount = params.get('requestAmount');
    const requestCurrency = params.get('requestCurrency');
    const reqDate = params.get('reqDate');
    const clientIp = params.get('clientIp');
    const webhookUrl = params.get('webhookUrl');
    const returnUrl = params.get('returnUrl');
    const mallReserved = params.get('mallReserved');
    const billkeyReq = params.get('billkeyReq');
    const appScheme = params.get('appScheme');

    setReqBody({
      orderId: String(orderId),
      productName: String(productName),
      requestCurrency: String(requestCurrency),
      requestAmount: Number(requestAmount),
      returnUrl: String(returnUrl),
      customerId: String(customerId),
      customerIp: String(clientIp),
      mallReserved: String(mallReserved),
      webhookUrl: String(webhookUrl),
    });

    updatePaymentState({
      mid,
      sign,
      auth,
      clientKey,
      orderId,
      customerId,
      productName,
      requestCurrency,
      requestAmount,
      reqDate,
      clientIp,
      webhookUrl,
      returnUrl,
      mallReserved,
      billkeyReq,
      appScheme,
    });

    if (auth) {
      window.sessionStorage.setItem('auth', auth);
    }
    if (mid) {
      window.sessionStorage.setItem('mid', mid);
    }
    if (clientKey) {
      window.sessionStorage.setItem('clientKey', clientKey);
    }
  }, []);

  useEffect(() => {
    if (reqBody) {
      getKeyinInfo.mutate();
    }
  }, [reqBody]);

  useEffect(() => {
    // langUse 사용유무에 따라 Y/N
    // Y > BO에 설정된 언어로 초기언어 설정
    // N > 브라우저 언어설정을 language로 설정 이외 언어시 BO 설정된 언어로 로딩
    if (language) {
      if (langUse === 'Y') {
        i18n.changeLanguage(language);
      } else {
        const userLanguage = navigator.language;
        const browserLanguage = browserTypeJson(userLanguage);
        if (browserLanguage !== '') {
          i18n.changeLanguage(browserLanguage);
          return;
        }
        i18n.changeLanguage(language);
      }
    } else {
      const userLanguage = navigator.language;
      const browserLanguage = browserTypeJson(userLanguage);
      if (browserLanguage !== '') {
        i18n.changeLanguage(browserLanguage);
        return;
      } else {
        i18n.changeLanguage('en');
      }
    }
  }, [language, langUse]);

  const handleAllClose = e => {
    e.preventDefault();

    const returnObject = {
      resultStatus: 'FAILED',
      resultCode: 'FAIL',
      resultMessage: 'Unprocessed payment.',
      tid: paymentState.tid,
      orderId: paymentState.orderId,
      mallReserved: paymentState.mallReserved,
      returnUrl: paymentState.returnUrl,
      schemeGroup: paymentState.schemeGroup,
      schemeCode: paymentState.schemeCode,
    };

    if (isMobile) {
      const url = new URL(paymentState.returnUrl);
      const params = new URLSearchParams(url.search);

      params.set('resultStatus', 'FAILED');
      params.set(
        'resultCode',
        paymentState.resultCode ? paymentState.resultCode : '',
      );
      params.set(
        'resultMessage',
        paymentState.resultMessage ? paymentState.resultMessage : '',
      );
      params.set('tid', paymentState.tid);
      params.set('orderId', paymentState.orderId);
      params.set(
        'mallReserved',
        paymentState?.mallReserved ? paymentState.mallReserved : '',
      );
      params.set(
        'schemeGroup',
        paymentState?.schemeGroup ? paymentState.schemeGroup : '',
      );
      params.set(
        'schemeCode',
        paymentState?.schemeCode ? paymentState.schemeCode : '',
      );
      window.location.href = url + '?' + params;
    } else {
      window.sessionStorage.removeItem('auth');
      window.sessionStorage.removeItem('mid');
      window.sessionStorage.removeItem('clientKey');
      IframeBridge.sendViewOff(returnObject);
    }
  };

  return (
    <>
      <div className="a-section">
        <PriceContent
          selectedCurrency={paymentState.requestCurrency}
          selectedValue={Number(paymentState.requestAmount)}
          requestCurrency={paymentState.requestCurrency}
          requestValue={Number(paymentState.requestAmount)}
        />
      </div>
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <KeyinForm keyinType={keyinType} keypadToken={keypadToken} />
      )}
    </>
  );
};

export default Keyin;
